import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import BSModal from 'react-bootstrap/Modal';
import styles from './Modal.module.scss';
import { dialogFocusOrder, isTopDialog, nextFocusIndex } from './Modal.helpers';

/* ────────────────────────────────────────────────────────────
 * Modal — centered, animated dialog with a gradient-accent rail.
 *
 *   <Modal open={open} onClose={() => setOpen(false)} title="Edit X">
 *     <ModalBody>...</ModalBody>
 *   </Modal>
 *
 * Prop unification (PROMPT_TEMPLATE.md §4 d2):
 *   Modal accepts both `open / onClose` (the unified system-wide
 *   prop pair, matches Drawer / Toast / CommandPalette) and the
 *   legacy `show / onHide` (Bootstrap-style, used by ~60+ callers
 *   shipped before unification). New code uses `open / onClose`.
 *   The legacy props will be removed in a future cleanup PR once
 *   every call site has migrated.
 *
 * For editor-modal body composition use the Modal sub-primitives
 * (ModalTitle, ModalBody, ModalSection, ModalGrid, ModalField,
 * ModalRow, ModalFooter) — they encode the editor contract once.
 * ──────────────────────────────────────────────────────────── */

const Modal = ({
  open,
  onClose,
  show,
  onHide,
  title,
  size,
  children,
  footer,
  className,
}) => {
  // Unified API wins; legacy is a fallback.
  const isOpen = open !== undefined ? open : show;
  const handleClose = onClose || onHide || (() => {});

  const classNames = [styles.modal, className || ''].filter(Boolean).join(' ');

  // react-bootstrap hands back its modal instance here; `.dialog` on it
  // is the real <div role="dialog"> node, which lives in a portal at
  // <body> and so cannot be reached with a ref on the wrapper below.
  const bsModalRef = useRef(null);

  /* Keep Tab inside the dialog.
   *
   * Bootstrap's own focus trap is off (see enforceFocus below), so
   * without this the Tab after the dialog's last button lands on the
   * sidebar behind the backdrop — visible, focusable, and impossible to
   * click, leaving a keyboard user stranded outside the dialog they are
   * in the middle of. Modal.helpers builds the ring of everything that
   * belongs to the dialog, popups portaled out of it included; here we
   * only take over at its two ends, and let the browser handle every
   * step in between exactly as it does today. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      // Somebody nearer the key press has already dealt with it (our
      // dropdowns do this to close their list first), or the key press
      // belongs to the browser (Ctrl+Tab switches tabs).
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;

      const dialog = bsModalRef.current && bsModalRef.current.dialog;
      if (!dialog) return;

      const { order, portaled } = dialogFocusOrder(dialog);
      const active = document.activeElement;
      const inside = dialog.contains(active) || portaled.has(active);

      if (!inside) {
        // Focus is somewhere else on the page. Reel it back in only
        // when it is loose (nothing focused, so Tab would restart from
        // the top of the document) or stranded in the page behind the
        // backdrop. Anything else — a SweetAlert popup, a date picker
        // from another library, a second dialog — owns its own focus
        // and must be left alone.
        const root = document.getElementById('root');
        const loose =
          !active || active === document.body || active === document.documentElement;
        const behind = !!root && !!active && root.contains(active);
        if (!loose && !behind) return;
        if (!isTopDialog(dialog)) return;
      }

      const next = nextFocusIndex({
        count: order.length,
        current: order.indexOf(active),
        shiftKey: e.shiftKey,
        force: portaled.has(active),
      });
      if (next == null) return;
      e.preventDefault();
      order[next].focus();
    };

    // On document, in the bubbling phase, so every handler closer to the
    // key press has already run: a dropdown that wants to close itself
    // and hand focus back to its trigger gets to do that first, and we
    // see where focus actually ended up.
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return (
    <div className={classNames}>
      {/* enforceFocus is disabled so focus can reach popups that portal
          outside the modal DOM (e.g. SearchableSelect / MultiSelect render
          their search input + option list into document.body — Bootstrap's
          focus trap would otherwise steal focus back and make the search
          field untypable). The Tab ring above replaces what the trap was
          there for, without fighting the portals. */}
      <BSModal
        ref={bsModalRef}
        show={isOpen}
        onHide={handleClose}
        size={size}
        centered
        enforceFocus={false}
      >
        <BSModal.Header closeButton>
          <BSModal.Title>{title}</BSModal.Title>
        </BSModal.Header>
        <BSModal.Body>{children}</BSModal.Body>
        {footer && <BSModal.Footer>{footer}</BSModal.Footer>}
      </BSModal>
    </div>
  );
};

Modal.propTypes = {
  // Unified prop pair (preferred for new code).
  open: PropTypes.bool,
  onClose: PropTypes.func,
  // Legacy prop pair (still supported; do not introduce in new code).
  show: PropTypes.bool,
  onHide: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  children: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
};

Modal.defaultProps = {
  open: undefined,
  onClose: undefined,
  show: false,
  onHide: undefined,
  title: '',
  size: 'md',
  children: null,
  footer: null,
  className: '',
};

export default Modal;
